import { useState } from 'react';
import { useForum } from '../useForum';
import { TextField, TextAreaField } from '@/components/forms';
import { AlertCircle, MailCheck, X, Wand2, Layers } from 'lucide-react';

interface CreateSubforumFormProps {
  onClose: () => void;
}

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Suggest a discussion category, from the public forum.
 *
 * THIS FORM CREATES NOTHING. It sends a suggestion to the organization's
 * admins, who approve or reject it; the category exists only if someone
 * approves. Every string in here is written to keep that true — see the success
 * state below, which is the one place it would be easiest and most damaging to
 * get wrong.
 */
export function CreateSubforumForm({ onClose }: CreateSubforumFormProps) {
  const { requestSubforum } = useForum();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [autoSlug, setAutoSlug] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleNameChange = (value: string) => {
    setName(value);
    if (autoSlug) {
      setSlug(generateSlug(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim() || !description.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      await requestSubforum(name.trim(), slug.trim(), description.trim());
      // The modal deliberately does NOT close here, and the fields are not
      // cleared. Closing on success is what the old create flow did, and it was
      // honest then: the subforum was there to go and look at. Now there is
      // nothing to look at, so the visitor has to be TOLD what happened —
      // closing silently would read as "done", and they would go hunting for a
      // category that does not exist.
      setSent(true);
    } catch (err) {
      // Whatever the API said, verbatim where it said anything. The full-queue
      // case in particular has a message worth reading: it is the difference
      // between "come back later" and a visitor retrying a form that cannot
      // succeed right now.
      setError(err instanceof Error ? err.message : 'Could not send that suggestion.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(9,9,7,.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          // Solid surface (not a translucent glass bg) so the modal is readable
          // over the dark backdrop in dark mode — see SCRUM-1008.
          width: '100%',
          maxWidth: 512,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--paper2)',
          border: '1px solid rgba(var(--ink-rgb),.13)',
          borderRadius: 18,
          boxShadow: '0 30px 70px rgba(9,9,7,.28)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Electric glow header band */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '22px 28px',
          borderBottom: '1px solid rgba(var(--ink-rgb),.13)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: 9,
              background: 'var(--chip)',
              color: '#9DF1FF',
            }}
          >
            <Layers size={17} />
          </span>
          <div>
            <h2
              style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: 18,
                letterSpacing: '-0.01em',
              }}
            >
              Suggest a Category
            </h2>
            <p
              style={{
                margin: '2px 0 0',
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                color: 'rgba(var(--ink-rgb),.62)',
              }}
            >
              Moderators review every suggestion before it appears
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="dc-h-icon"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            borderRadius: 9,
            cursor: 'pointer',
            background: 'transparent',
            border: '1px solid rgba(var(--ink-rgb),.16)',
            color: 'rgba(var(--ink-rgb),.62)',
            transition: 'border-color .2s, background .2s, color .2s',
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {sent ? (
        /* SENT FOR REVIEW — never "created".
           Nothing exists yet. A visitor told their category was created goes
           looking for it, does not find it, and concludes the site is broken;
           the truth costs one extra sentence and avoids all of that. The name
           is echoed back so they can see exactly what went in, and the modal
           stays open until they dismiss it themselves. */
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'grid',
            gap: 20,
            padding: 28,
            justifyItems: 'center',
            textAlign: 'center',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 46,
              height: 46,
              borderRadius: 9,
              background: 'var(--chip)',
              color: '#9DF1FF',
            }}
          >
            <MailCheck size={20} />
          </span>

          <div>
            <h3
              style={{
                margin: '0 0 8px',
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: 18,
                letterSpacing: '-0.01em',
              }}
            >
              Sent for review
            </h3>
            <p
              style={{
                margin: '0 auto',
                maxWidth: 380,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: 'rgba(var(--ink-rgb),.66)',
              }}
            >
              Thanks &mdash; your suggestion of{' '}
              <strong style={{ color: 'var(--ink)' }}>{name.trim()}</strong> has
              gone to this forum&rsquo;s moderators. It has not been added yet:
              if they approve it, the category will appear on the forum.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="dc-h-btn-sm"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: 'var(--btn)',
              color: 'var(--btnfg)',
              border: 'none',
              borderRadius: 999,
              padding: '11px 24px',
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              transition: 'transform .2s, box-shadow .2s',
            }}
          >
            Done
          </button>
        </div>
      ) : (
      <form style={{ display: "grid", gap: 20, padding: 28 }} onSubmit={handleSubmit}>
        <TextField
          label="Name"
          required
          value={name}
          onChange={handleNameChange}
          placeholder="e.g., General Discussion"
          maxLength={50}
        />

        <TextField
          label="Slug"
          required
          value={slug}
          onChange={(value) => {
            setSlug(value);
            setAutoSlug(false);
          }}
          placeholder="e.g., general-discussion"
          maxLength={50}
          // The hyphen is ESCAPED, and has to be. Chrome compiles `pattern`
          // with the RegExp `v` flag, which rejects a bare `-` at the end of a
          // character class as an invalid class — the browser then throws
          // "Invalid regular expression" and IGNORES the attribute, so the
          // unescaped version validated nothing at all while looking like it
          // did. Found by the console error it logs on every render.
          pattern="[a-z0-9\-]+"
          hint="URL-friendly identifier (lowercase, hyphens only)"
          action={
            <button
              type="button"
              onClick={() => {
                setAutoSlug(true);
                setSlug(generateSlug(name));
              }}
              className="dc-h-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                fontFamily: "'Manrope', sans-serif",
                fontSize: 12,
                color: 'var(--link)',
                transition: 'color .2s',
              }}
            >
              <Wand2 size={12} /> Auto
            </button>
          }
        />

        {/* Live URL preview */}
        {/* Wraps, and breaks a long slug, so a slug up to its 50-character
            limit stays inside the dialog on a phone (T101026002). */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
            overflowWrap: 'anywhere',
            borderRadius: 10,
            padding: '10px 14px',
            border: '1px solid rgba(var(--ink-rgb),.16)',
            fontFamily: "'Manrope', sans-serif",
            fontSize: 12,
            color: 'rgba(var(--ink-rgb),.62)',
          }}
        >
          {/* "If approved", not "Preview" — the old label showed an address
              that would exist the moment you submitted. This one will not
              exist unless a moderator says so. */}
          <span>If approved:</span>
          <code style={{ fontFamily: "'Manrope', sans-serif", minWidth: 0 }}>
            /forum/
            <span style={{ color: 'var(--link)' }}>{slug || 'your-slug'}</span>
          </code>
        </div>

        <TextAreaField
          label="Description"
          required
          value={description}
          onChange={setDescription}
          placeholder="Describe what this subforum is about..."
          maxLength={200}
          showCount
          rows={3}
          className="resize-none"
        />

        {error && (
          /* The API's own words, not a generic failure. A full queue ("not
             accepting suggestions right now") tells the visitor to come back;
             "something went wrong" tells them to hammer a button that cannot
             work yet. `role="alert"` so a screen reader hears it — the message
             appears without any focus change. */
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              borderRadius: 10,
              padding: '12px 14px',
              border: '1px solid rgba(var(--ink-rgb),.16)',
              background: 'var(--chip)',
              fontFamily: "'Manrope', sans-serif",
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--ink)',
            }}
          >
            <AlertCircle size={16} style={{ flex: 'none', marginTop: 2 }} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            paddingTop: 18,
            borderTop: '1px solid rgba(var(--ink-rgb),.2)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="dc-h-ghost"
            style={{
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid rgba(var(--ink-rgb),.3)',
              borderRadius: 999,
              padding: '10px 22px',
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              color: 'rgba(var(--ink-rgb),.66)',
              transition: 'border-color .2s, background .2s, color .2s',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || !slug.trim() || !description.trim() || submitting}
            className="dc-h-btn-sm"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: 'var(--btn)',
              color: 'var(--btnfg)',
              border: 'none',
              borderRadius: 999,
              padding: '11px 24px',
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              transition: 'transform .2s, box-shadow .2s',
            }}
          >
            {submitting ? 'Sending…' : 'Send suggestion'}
          </button>
        </div>
      </form>
      )}
      </div>
    </div>
  );
}
